/**
 * QUIL: non-blocking sync notices (W11, app-platform design/W11-sync-orchestration.md §3.6).
 *
 * The quiet companion to the ConflictDialog. A notice says something the sync engine did on the
 * writer's behalf that overrode something she did, and is never a modal:
 *  - an edit brought back a chapter or story another device had deleted (decision 1, the edit
 *    wins), offering **Delete again**;
 *  - a delete was refused because another device had edited it since (decision 2), so it was kept;
 *  - a chapter deleted on another device was removed here too, with a copy kept in Recently deleted
 *    (ledger #9: it used to vanish with no word).
 *
 * Deliberately tiny and dependency-free: it sits in the entry chunk (sync.ts pushes into it), while
 * the wording (the shared `describeNotice`) and the list itself load lazily with the first notice.
 * In memory only, like inkling's pilot: a notice reports what just happened, and the data it
 * describes is already safe in Dexie and on OneDrive either way.
 */

import type { ConflictRecord } from './syncConflict'

export interface SyncNotice {
  id: string
  /** The record the shared `describeNotice` words, or, for `kind: 'followed-delete'`, the chapter
   *  the other device deleted. */
  record: ConflictRecord<string>
  /** `'followed-delete'`: Quil's own notice (ledger #9), worded in the list, not by the shared model. */
  kind: 'shared' | 'followed-delete'
  /** The unit, for "Delete again". */
  unit: { kind: 'chapter' | 'work'; workId: string; chapterId?: string }
  at: number
}

type Listener = () => void

let notices: SyncNotice[] = []
const listeners = new Set<Listener>()
let seq = 0

function emit(): void {
  for (const l of listeners) l()
}

/** Add a notice. A newer notice for the same unit replaces the older one. */
export function pushSyncNotice(n: Omit<SyncNotice, 'id' | 'at'>): void {
  const id = `notice-${Date.now().toString(36)}-${(seq += 1)}`
  notices = [...notices.filter(x => x.record.key !== n.record.key), { ...n, id, at: Date.now() }]
  emit()
}

export function dismissSyncNotice(id: string): void {
  const next = notices.filter(n => n.id !== id)
  if (next.length === notices.length) return
  notices = next
  emit()
}

export function getSyncNotices(): SyncNotice[] {
  return notices
}

export function subscribeSyncNotices(l: Listener): () => void {
  listeners.add(l)
  return () => { listeners.delete(l) }
}

/** Test-only: forget every notice.
 * @internal Test seam: resets module state between tests. Never imported by the app.
 */
export function resetSyncNotices(): void {
  notices = []
  emit()
}
